import React, { useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { CheckCircle, ErrorOutline, KeyboardArrowRight } from "@mui/icons-material";
import { TherapyType } from "../../../api-client";
import { useCatalogGraph } from "../../../stores/MasterTables/therapies.queries";

/**
 * "Katalog" — the resolution chain end to end.
 *
 * The question this tab exists to answer is the one nothing else in the app
 * answers: *if a doctor writes this term on a prescription for this payer, will
 * scheduling find something to bill?* Before MD-232 that was implicit, because a
 * prescription line named a concrete therapy. Now it is resolved at scheduling
 * time from term + tarif group + credentials, so a gap only shows up as
 * "Keine abrechenbare Leistung" in front of a user with a patient waiting.
 *
 * The matrix makes it visible up front: one row per term, one column per tarif
 * group, a tick where the chain reaches a billable row.
 */

interface Props {
  therapyType: TherapyType;
}

const CatalogTab: React.FC<Props> = ({ therapyType }) => {
  const { data: graph, isLoading } = useCatalogGraph(therapyType);
  const [search, setSearch] = useState("");
  const [onlyProblems, setOnlyProblems] = useState(false);

  const terms: any[] = (graph as any)?.terms ?? [];
  const tarifGroups: string[] = (graph as any)?.tarifGroups ?? [];
  const issues: any = (graph as any)?.issues ?? {};

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = terms;
    if (q) {
      list = list.filter(
        (t) =>
          t.id.toLowerCase().includes(q) ||
          t.label.toLowerCase().includes(q) ||
          (t.aliases ?? []).some((a: string) => a.toLowerCase().includes(q))
      );
    }
    if (onlyProblems) {
      list = list.filter(
        (t) => (t.unreachableInTarifGroups ?? []).length > 0 || (t.resolutions ?? []).length === 0
      );
    }
    return list;
  }, [terms, search, onlyProblems]);

  const issueBlocks: { key: string; label: string; hint: string }[] = [
    {
      key: "termsWithoutResolution",
      label: "Begriffe ohne Methode",
      hint: "Auf keiner Verordnung nutzbar — es gibt nichts abzurechnen.",
    },
    {
      key: "termsWithoutAlias",
      label: "Begriffe ohne Schreibweise",
      hint: "Die Texterkennung findet sie nie, egal was im Heilmittelfeld steht.",
    },
    {
      key: "methodsWithoutLineItem",
      label: "Methoden ohne Abrechnungszeile",
      hint: "Auflösungen auf diese Methoden laufen ins Leere.",
    },
    {
      key: "methodsWithoutTerm",
      label: "Methoden ohne Begriff",
      hint: "Von einer Verordnung aus nicht erreichbar.",
    },
    {
      key: "lineItemsWithoutMethod",
      label: "Aktive Abrechnungszeilen ohne Methode",
      hint: "Nicht über die Auflösung erreichbar — meist Altbestand.",
    },
  ];

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <TextField
          size="small"
          label="Suchen (Begriff, Schreibweise)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 320 }}
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={onlyProblems}
              onChange={(e) => setOnlyProblems(e.target.checked)}
            />
          }
          label="Nur Lücken"
        />
        <Box sx={{ flex: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {filtered.length} von {terms.length} Begriffen
        </Typography>
      </Stack>

      {issueBlocks.some((b) => (issues[b.key] ?? []).length > 0) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Strukturelle Lücken</AlertTitle>
          <Stack spacing={1}>
            {issueBlocks.map((b) => {
              const ids: string[] = issues[b.key] ?? [];
              if (!ids.length) return null;
              return (
                <Box key={b.key}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {b.label} ({ids.length}) — {b.hint}
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                    {ids.slice(0, 25).map((id) => (
                      <Chip key={id} label={id} size="small" variant="outlined" />
                    ))}
                    {ids.length > 25 && (
                      <Chip label={`+${ids.length - 25} weitere`} size="small" />
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        Ein Haken bedeutet: schreibt der Arzt diesen Begriff auf eine Verordnung dieser
        Tarifgruppe, findet die Terminplanung eine abrechenbare Leistung. Ein Kreuz bedeutet, der
        Nutzer bekommt beim Verplanen „Keine abrechenbare Leistung“.
      </Alert>

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: "60vh" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 200 }}>Begriff</TableCell>
              <TableCell sx={{ minWidth: 220 }}>Auflösung (beste zuerst)</TableCell>
              {tarifGroups.map((g) => (
                <TableCell key={g} align="center" sx={{ whiteSpace: "nowrap" }}>
                  {g}
                </TableCell>
              ))}
              <TableCell align="center">Verwendung</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((term) => {
              const unreachable = new Set<string>(term.unreachableInTarifGroups ?? []);
              return (
                <TableRow key={term.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                      {term.id}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {term.label}
                    </Typography>
                    {/* The written forms a doctor may use. Without one the row can
                        never be reached, however well it resolves further down. */}
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                      {(term.aliases ?? []).length === 0 ? (
                        <Tooltip title="Ohne Schreibweise wird der Begriff nie erkannt">
                          <Chip label="keine Schreibweise" size="small" color="warning" />
                        </Tooltip>
                      ) : (
                        (term.aliases ?? []).map((a: string) => (
                          <Chip
                            key={a}
                            label={a}
                            size="small"
                            variant="outlined"
                            sx={{ fontFamily: "monospace", height: 20, fontSize: "0.7rem" }}
                          />
                        ))
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {(term.resolutions ?? []).length === 0 ? (
                      <Chip label="keine Methode" size="small" color="warning" />
                    ) : (
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                      >
                        {(term.resolutions ?? []).map((r: any, idx: number) => (
                          <React.Fragment key={r.therapyMethodId}>
                            {idx > 0 && <KeyboardArrowRight fontSize="small" color="disabled" />}
                            <Tooltip title={`${r.methodLabel} · Priorität ${r.priority}`}>
                              <Chip
                                label={r.therapyMethodId}
                                size="small"
                                variant="outlined"
                                color={idx === 0 ? "primary" : "default"}
                              />
                            </Tooltip>
                          </React.Fragment>
                        ))}
                      </Stack>
                    )}
                  </TableCell>
                  {tarifGroups.map((g) => (
                    <TableCell key={g} align="center">
                      {unreachable.has(g) ? (
                        <Tooltip title={`Nicht abrechenbar in ${g}`}>
                          <ErrorOutline fontSize="small" color="disabled" />
                        </Tooltip>
                      ) : (
                        <Tooltip title={`Abrechenbar in ${g}`}>
                          <CheckCircle fontSize="small" color="success" />
                        </Tooltip>
                      )}
                    </TableCell>
                  ))}
                  <TableCell align="center">
                    <Chip
                      label={term.usedByLines}
                      size="small"
                      variant="outlined"
                      color={term.usedByLines ? "primary" : "default"}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CatalogTab;
